/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import versions from "./data";
import RecipesDisplay from "./components/RecipesDisplay";
import WorkflowsDisplay from "./components/WorkflowsDisplay";
import PinnedCurl from "./components/PinnedCurl";
import SampleIdDisplay from "./components/SampleIdDisplay";
import SequenceDiagrams from "./components/SequenceDiagrams";
import logoImg from "./assets/FrankieOne-MainLogo-2.webp";
import IntegrationDiagrams from "./components/IntegrationDiagrams";
import { apiRequest, helpDocTemplatesApi } from "@api/client";
import type { HelpDocTemplate } from "@api/client";
const STORAGE_PREFIX = "hdb_";

// -- Persistence helpers -----------------------------------------------------
function loadClientState(name) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + name);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveClientState(name, state) {
  if (!name.trim()) return;
  try { localStorage.setItem(STORAGE_PREFIX + name, JSON.stringify(state)); } catch {}
}

function loadGlobal(key, fallback) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + "global_" + key);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
}

function saveGlobal(key, value) {
  try { localStorage.setItem(STORAGE_PREFIX + "global_" + key, JSON.stringify(value)); } catch {}
}

// -- Template helpers ----------------------------------------------------------
const PLACEHOLDER = "{{CLIENT_NAME}}";

// ============================================================================
export function HelpDocBuilder() {
  const [apiVersion, setApiVersion] = useState(() => loadGlobal("apiVersion", "v1"));
  const [clientName, setClientName] = useState(() => loadGlobal("clientName", ""));
  const [mode, setMode]             = useState("both");
  const [checks, setChecks]         = useState({ kyc: true, kyb: true, idv: true, aml: true, risk: false, sso: false });
  const [hasChildId, setHasChildId] = useState(false);
  const [active, setActive]         = useState("overview");
  const [content, setContent]       = useState({});
  const [status, setStatus]         = useState({});
  const [feedback, setFeedback]     = useState({});
  const [editBuf, setEditBuf]       = useState({});
  const [editing, setEditing]       = useState({});
  const [pdfMode, setPdfMode]       = useState(false);
  const [templates, setTemplates]   = useState<HelpDocTemplate[]>([]);
  const skipLoad = useRef(false);


  // Load saved state when client name changes (debounced)
  useEffect(() => {
    if (skipLoad.current) { skipLoad.current = false; return; }
    if (!clientName.trim()) return;
    const timer = setTimeout(() => {
      const saved = loadClientState(clientName.trim());
      if (saved) {
        if (saved.content) setContent(saved.content);
        if (saved.status) setStatus(saved.status);
        if (saved.feedback) setFeedback(saved.feedback);
        if (saved.mode) setMode(saved.mode);
        if (saved.checks) setChecks(prev => ({ ...prev, ...saved.checks }));
        if (saved.hasChildId !== undefined) setHasChildId(saved.hasChildId);
        if (saved.apiVersion) setApiVersion(saved.apiVersion);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [clientName]);

  // Auto-save to localStorage when state changes
  useEffect(() => {
    if (!clientName.trim()) return;
    const timer = setTimeout(() => {
      saveClientState(clientName.trim(), { content, status, feedback, mode, checks, hasChildId, apiVersion });
    }, 300);
    return () => clearTimeout(timer);
  }, [clientName, content, status, feedback, mode, checks, hasChildId, apiVersion]);

  // Persist client name and version globally
  useEffect(() => { saveGlobal("clientName", clientName); }, [clientName]);
  useEffect(() => { saveGlobal("apiVersion", apiVersion); }, [apiVersion]);

  // Fetch templates from API on mount and when apiVersion changes
  useEffect(() => {
    helpDocTemplatesApi.list(apiVersion).then(res => {
      if (res.success && res.data) setTemplates(res.data.data || []);
    });
  }, [apiVersion]);

  // Derive version-specific data
  const ver = versions[apiVersion];
  const { SECTIONS, STATUS_CONFIG } = ver.constants;
  const { buildSystemPrompt, buildPrompt } = ver.prompts;
  const { RECIPES, CHECK_BADGE, ORG_WORKFLOWS } = ver.recipes;

  const STATIC_SECTIONS = new Set(["recipes", "workflows", "diagrams", "int_diagrams"]);
  const isStatic = (id) => STATIC_SECTIONS.has(id);

  // Mode toggles — "portal" mode hides API content, "api" mode hides portal content
  const apiDocs = mode !== "portal";
  const portalDocs = mode !== "api";

  // Section visibility: respect both check toggles and apiDocs toggle
  const isSectionVisible = (s) => {
    if (checks.hasOwnProperty(s.id) && !checks[s.id]) return false;
    if (s.apiOnly && !apiDocs) return false;
    return true;
  };
  const visibleSections = SECTIONS.filter(isSectionVisible);

  // If the active section gets hidden (unchecked or mode changed), switch to the first visible section
  useEffect(() => {
    const activeSec = SECTIONS.find(s => s.id === active);
    const isHidden = activeSec && !isSectionVisible(activeSec);
    if (isHidden) {
      const first = SECTIONS.find(s => isSectionVisible(s));
      if (first) setActive(first.id);
    }
  }, [checks, mode, active]);


  const switchVersion = (v) => {
    skipLoad.current = true;
    setApiVersion(v);
    setActive("overview");
    setContent({});
    setStatus({});
    setFeedback({});
    setEditBuf({});
    setEditing({});
    setPdfMode(false);
  };

  // -- Template helpers (read from local cache) --------------------------------
  const getTemplateList = (version, sectionId) =>
    templates.filter(t => t.api_version === version && t.section_id === sectionId);

  const hasTemplateForSection = (version, sectionId) =>
    templates.some(t => t.api_version === version && t.section_id === sectionId);

  const loadTemplateHtml = (version, sectionId, client, name) => {
    const list = getTemplateList(version, sectionId);
    const entry = name ? list.find(t => t.name === name) : list[0];
    if (!entry) return null;
    return client.trim()
      ? entry.html.replaceAll(PLACEHOLDER, client.trim())
      : entry.html;
  };

  // -- Template actions (API-backed) ------------------------------------------
  const saveTpl = async (id) => {
    const name = prompt("Template name:", "Default");
    if (!name) return;
    const html = clientName.trim()
      ? (content[id] || "").replaceAll(clientName.trim(), PLACEHOLDER)
      : (content[id] || "");
    const res = await helpDocTemplatesApi.save({ apiVersion, sectionId: id, name: name.trim(), html });
    if (res.success && res.data?.data) {
      const saved = res.data.data;
      setTemplates(prev => {
        const idx = prev.findIndex(t => t.api_version === apiVersion && t.section_id === id && t.name === name.trim());
        if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
        return [...prev, saved];
      });
    }
  };

  const loadTpl = (id, name) => {
    const html = loadTemplateHtml(apiVersion, id, clientName, name);
    if (html) {
      setContent(p => ({ ...p, [id]: html }));
      setStatus(p => ({ ...p, [id]: "ready" }));
    }
  };

  const deleteTpl = async (id, name) => {
    const entry = templates.find(t => t.api_version === apiVersion && t.section_id === id && t.name === name);
    if (!entry) return;
    const res = await helpDocTemplatesApi.remove(entry.id);
    if (res.success) {
      setTemplates(prev => prev.filter(t => t.id !== entry.id));
    }
  };

  const loadAllTpls = () => {
    const updates = {};
    const statusUpdates = {};
    for (const s of visibleSections) {
      if (isStatic(s.id)) continue;
      const html = loadTemplateHtml(apiVersion, s.id, clientName);
      if (html) {
        updates[s.id] = html;
        statusUpdates[s.id] = "ready";
      }
    }
    if (Object.keys(updates).length > 0) {
      setContent(p => ({ ...p, ...updates }));
      setStatus(p => ({ ...p, ...statusUpdates }));
    }
  };

  const tplCount = visibleSections.filter(s => !isStatic(s.id) && hasTemplateForSection(apiVersion, s.id)).length;

  // -- API call ----------------------------------------------------------------
  const canGenerate = clientName.trim().length > 0;

  const gen = async (id: string, useFb = false) => {
    if (!canGenerate) return;
    setStatus((p: any) => ({ ...p, [id]: "generating" }));
    const systemCtx = buildSystemPrompt();
    const userPrompt = buildPrompt(id, clientName, mode, checks, hasChildId, useFb ? (feedback[id] || "") : "");
    const prompt = `${systemCtx}\n\n---\n\n${userPrompt}`;

    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await apiRequest<{ content: string | null }>('/help-doc-generate', {
          method: 'POST',
          body: JSON.stringify({ prompt }),
        });
        const text = res.data?.data?.content || null;
        if (text) {
          setContent((p: any) => ({ ...p, [id]: text }));
          setStatus((p: any) => ({ ...p, [id]: "ready" }));
          return;
        }
        if (attempt === 0) continue;
      } catch (e: any) {
        if (attempt === 0) continue;
        setContent((p: any) => ({ ...p, [id]: `<p style="color:red">Error: ${e.message}</p>` }));
        setStatus((p: any) => ({ ...p, [id]: "error" }));
        return;
      }
    }
    setContent((p: any) => ({ ...p, [id]: "<p>No content returned after retrying. Try again.</p>" }));
    setStatus((p: any) => ({ ...p, [id]: "error" }));
  };

  const genAll = async () => {
    for (const s of visibleSections.filter(s => !isStatic(s.id))) {
      await gen(s.id);
    }
  };
  const startEdit = id => { setEditBuf(p => ({ ...p, [id]: content[id] })); setEditing(p => ({ ...p, [id]: true })); };
  const saveEdit  = id => { setContent(p => ({ ...p, [id]: editBuf[id] || p[id] })); setEditing(p => ({ ...p, [id]: false })); };
  const approve   = id => { if (editing[id]) saveEdit(id); setStatus(p => ({ ...p, [id]: "approved" })); };

  const KEY_GROUPS = {
    kyc: "KYC",
    idv: "IDV",
    kyb: "KYB",
    aml: "AML",
    risk: "RISK",
    sso: "SSO",
  };
  const KEY_COLORS = {
    KYC: { bg: "#e8f5e9", border: "#43a047", accent: "#2e7d32" },
    AML: { bg: "#fff3e0", border: "#ef6c00", accent: "#e65100" },
    KYB: { bg: "#e3f2fd", border: "#1e88e5", accent: "#1565c0" },
    IDV: { bg: "#f3e5f5", border: "#8e24aa", accent: "#6a1b9a" },
    RISK: { bg: "#fef3c7", border: "#d97706", accent: "#b45309" },
    SSO: { bg: "#e0f2fe", border: "#0284c7", accent: "#0369a1" },
  };

  const approvedCount = visibleSections.filter(s => status[s.id] === "approved" || isStatic(s.id)).length;
  const sec = SECTIONS.find(s => s.id === active);
  const st  = status[active];
  const sc  = STATUS_CONFIG[st] || STATUS_CONFIG.undefined;
  const activeTpl = !isStatic(active) && hasTemplateForSection(apiVersion, active);
  const activeTplList = !isStatic(active) ? getTemplateList(apiVersion, active) : [];
  const isSavedAsTpl = activeTplList.length > 0 && (() => {
    const cur = clientName.trim() ? (content[active] || "").replaceAll(clientName.trim(), PLACEHOLDER) : (content[active] || "");
    return activeTplList.some(t => t.html === cur);
  })();

  // -- PDF export view --------------------------------------------------------
  if (pdfMode) {
    const pdfTitle = clientName?.trim()
      ? `FrankieOne ${ver.label} Testing Guide - ${clientName.trim()}`
      : `FrankieOne ${ver.label} Testing Guide`;
    document.title = pdfTitle;
    return createPortal(
      <div id="pdf-portal" style={{ position: "fixed", inset: 0, zIndex: 99999, fontFamily: "Arial, sans-serif", background: "white", color: "#1e293b", overflow: "auto" }}>
        <div className="no-print" style={{ padding: "10px 20px", background: "#1a1a2e", display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={() => { document.title = "Help Doc Builder"; setPdfMode(false); }} style={{ padding: "6px 12px", background: "rgba(255,255,255,0.1)", color: "white", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>← Back to Editor</button>
          <button onClick={() => {
            const noSplit = document.querySelectorAll(
              ".pdf-content pre, .pdf-content table, .pdf-content img, .pdf-content svg, .pdf-content h2, .pdf-content h3"
            );
            noSplit.forEach(el => { el.style.breakInside = "avoid"; el.style.pageBreakInside = "avoid"; });
            document.querySelectorAll(".pdf-content div").forEach(el => {
              const s = el.getAttribute("style") || "";
              if (s.includes("border")) {
                el.style.breakInside = "avoid";
                el.style.pageBreakInside = "avoid";
              }
            });
            document.querySelectorAll(".pdf-content h2, .pdf-content h3, .pdf-content h4").forEach(el => {
              el.style.breakAfter = "avoid";
              el.style.pageBreakAfter = "avoid";
            });
            setTimeout(() => window.print(), 50);
          }} style={{ padding: "6px 14px", background: "#e94560", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Print / Save as PDF</button>
          <span style={{ fontSize: 11, color: "#aaa" }}>Print → Destination: Save as PDF</span>
        </div>
        <style>{`
          @page { margin: 10mm 10mm; }
          @media print {
            :root, .dark { --bg-primary: #ffffff !important; --bg-secondary: #ffffff !important; --bg-tertiary: #f9fafb !important; }
            html, body { background: white !important; background-color: white !important; }
            body > *:not(#pdf-portal) { display: none !important; }
            .no-print { display: none !important; }
            #pdf-portal { display: block !important; position: static !important; overflow: visible !important; }
            .page-break { page-break-before: always !important; break-before: page !important; }
            * { orphans: 3 !important; widows: 3 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
          }
          ${!hasChildId ? '.child-id-only { display: none !important; }' : ''}
          ${!apiDocs ? '.api-docs-only { display: none !important; }' : ''}
          ${!portalDocs ? '.portal-docs-only { display: none !important; }' : ''}
          .pdf-content h2 { color: #4285fb !important; border-bottom-color: #4285fb !important; font-size: 16px !important; }
          .pdf-content h3 { font-size: 13px !important; }
          .pdf-content p, .pdf-content li, .pdf-content td, .pdf-content th { font-size: 11px !important; }
          .pdf-content pre, .pdf-content code { font-size: 10px !important; }
          .pdf-content table { font-size: 11px !important; }
        `}</style>
        <div style={{ maxWidth: 800, margin: "0 auto", padding: 40, fontSize: 11 }}>
          <div style={{ textAlign: "center", padding: "50px 0 40px", borderBottom: "3px solid #4285fb", marginBottom: 40 }}>
            <img src={logoImg} alt="FrankieOne" style={{ height: 36, marginBottom: 16 }} />
            <h1 style={{ margin: "0 0 8px", fontSize: 28, fontWeight: 700, color: "#4285fb" }}>{ver.label} Integration Guide</h1>
            {clientName && <p style={{ margin: "4px 0", fontSize: 13, color: "#555" }}>Prepared for: <strong>{clientName}</strong></p>}
            <p style={{ margin: "4px 0", fontSize: 11, color: "#999" }}>Generated {new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}</p>
          </div>
          <div style={{ marginBottom: 40 }}>
            <h2 style={{ color: "#4285fb", fontSize: 16, marginBottom: 14 }}>Quick Links</h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {visibleSections.map(s => {
                const kg = KEY_GROUPS[s.id];
                const kc = kg ? KEY_COLORS[kg] : null;
                return (
                  <a key={s.id} href={`#section-${s.id}`} style={{ padding: "8px 14px", background: kc ? kc.border : "#4285fb", color: "white", borderRadius: 4, fontSize: 12, textDecoration: "none", fontWeight: kc ? 700 : 500 }}>
                    {s.emoji} {s.title}
                  </a>
                );
              })}
            </div>
          </div>
          {visibleSections.map((s, i) => {
            return (
            <div key={s.id} id={`section-${s.id}`} className={`pdf-content${i > 0 ? " page-break" : ""}`} style={{ marginBottom: 48, position: "relative" }}>
              <img src={logoImg} alt="" style={{ position: "absolute", top: 0, right: 0, height: 22, opacity: 0.8 }} />
              {(s.id === "recipes" || s.id === "workflows")
                ? (s.id === "workflows"
                  ? <WorkflowsDisplay recipes={RECIPES} checkBadge={CHECK_BADGE} />
                  : <RecipesDisplay pending={false} recipes={RECIPES} checkBadge={CHECK_BADGE} />)
                : s.id === "diagrams"
                ? <SequenceDiagrams checks={checks} apiVersion={apiVersion} />
                : s.id === "int_diagrams"
                ? <IntegrationDiagrams checks={checks} />
                : <div dangerouslySetInnerHTML={{ __html: content[s.id] || "<p style='color:#bbb;font-style:italic'>Section not generated.</p>" }} />
              }
              {s.id === "overview" && apiDocs && <PinnedCurl apiVersion={apiVersion} />}
              {s.id === "idv" && <SampleIdDisplay />}
              {s.id === "kyb" && ORG_WORKFLOWS && ORG_WORKFLOWS.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <h3 style={{ color: "#333", marginBottom: 10 }}>Organisation Workflows</h3>
                  {ORG_WORKFLOWS.map(wf => (
                    <div key={wf.key} style={{ border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 10, padding: "10px 14px", pageBreakInside: "avoid" }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{wf.name}</span>
                        <code className="api-docs-only" style={{ background: "#e8f0ff", padding: "1px 6px", borderRadius: 3, fontSize: 10, color: "#4285fb" }}>{wf.key}</code>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {Object.entries(wf.steps).filter(([cat]) => cat !== "DECISION").map(([cat, items]) => {
                          const s = (CHECK_BADGE && CHECK_BADGE[cat]) || { bg: "#f3f4f6", color: "#374151" };
                          const label = cat === "AML" ? "PEPs + Sanctions" : cat === "ORGANIZATIONDATAFETCH" ? "Org Data Fetch" : cat === "ORGANIZATIONPERSIST" ? "Org Persist" : cat;
                          return (
                            <span key={cat} style={{ background: s.bg, color: s.color, padding: "2px 8px", borderRadius: 10, fontSize: 9, fontWeight: 600, whiteSpace: "nowrap" }}>
                              {label}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>,
      document.body
    );
  }

  // -- Editor view ------------------------------------------------------------
  return (
    <div className="flex rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] overflow-hidden" style={{ height: "calc(100vh - 160px)", minHeight: 500 }}>
      <style>{`
        .hdb-content h2 { font-weight: 700 !important; font-size: 1.3em !important; color: var(--color-primary) !important; border-bottom-color: var(--color-primary) !important; margin-top: 28px !important; }
        .hdb-content h3 { font-weight: 700 !important; font-size: 1.1em !important; color: var(--text-primary) !important; margin-top: 22px !important; }
        .hdb-content h4 { font-weight: 600 !important; font-size: 1em !important; }
        .hdb-content a { color: var(--color-primary) !important; }
        .hdb-content p { line-height: 1.7; margin: 10px 0; }
        .hdb-content ul, .hdb-content ol { line-height: 1.7; margin: 10px 0 14px; }
        .hdb-content p + p { margin-top: 14px; }
        .hdb-content h2, .hdb-content h3, .hdb-content h4 { margin-bottom: 10px !important; }
        .hdb-content h3 + p, .hdb-content h2 + p { margin-top: 4px; }
        .hdb-content div[style] + div[style] { margin-top: 14px; }
        .hdb-content strong { display: inline; }
        .dark .hdb-content * {
          color: var(--text-primary) !important;
          border-color: var(--border-color) !important;
        }
        .dark .hdb-content div[style*="background"]:not([style*="border-left"]),
        .dark .hdb-content span[style*="background"]:not([style*="border-radius:50%"]),
        .dark .hdb-content p[style*="background"] {
          background: var(--bg-tertiary) !important;
        }
        .dark .hdb-content div[style*="border-left"] { color: #1e293b !important; }
        .dark .hdb-content div[style*="border-left"] * { color: #1e293b !important; }
        .dark .hdb-content div[style*="border-left"] code { color: #4285fb !important; }
        .dark .hdb-content th, .dark .hdb-content th[style] { background: var(--bg-tertiary) !important; }
        .dark .hdb-content td, .dark .hdb-content td[style] { background: var(--bg-primary) !important; }
        .dark .hdb-content pre, .dark .hdb-content pre[style] { background: #1e293b !important; color: #93c5fd !important; }
        .dark .hdb-content pre *, .dark .hdb-content pre code { color: #93c5fd !important; background: transparent !important; }
        .dark .hdb-content code, .dark .hdb-content code[style] { background: var(--bg-tertiary) !important; color: var(--color-primary) !important; }
        .dark .hdb-content a { color: var(--color-primary) !important; }
        .dark .hdb-content span[style*="background:#43a047"], .dark .hdb-content span[style*="background: #43a047"], .dark .hdb-content span[style*="background: rgb(67, 160, 71)"] {
          background: #43a047 !important; color: white !important;
        }
        ${!hasChildId ? '.child-id-only { display: none !important; }' : ''}
        ${!apiDocs ? '.api-docs-only { display: none !important; }' : ''}
          ${!portalDocs ? '.portal-docs-only { display: none !important; }' : ''}
        @keyframes hdb-spin { to { transform: rotate(360deg) } }
        @keyframes hdb-pulse { 0%,100% { opacity:.4 } 50% { opacity:1 } }
      `}</style>

      {/* Section nav panel */}
      <div className="w-56 shrink-0 flex flex-col border-r border-[var(--border-color)] bg-[var(--bg-secondary)]">
        {/* Controls header */}
        <div className="p-3 border-b border-[var(--border-color)] space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-[var(--text-primary)]">{ver.label} Pack Builder</span>
            <div className="flex gap-1">
              {Object.entries(versions).map(([key, v]) => (
                <button key={key} onClick={() => switchVersion(key)}
                  className={`px-2 py-0.5 rounded text-[10px] font-semibold border-none cursor-pointer transition-colors ${
                    apiVersion === key
                      ? "bg-[var(--color-primary)] text-white"
                      : "bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                  }`}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Client name..."
            className="w-full px-2 py-1.5 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] text-xs placeholder:text-[var(--text-muted)]" />

          <div className="flex gap-1.5">
            <select value={mode} onChange={e => setMode(e.target.value)}
              className="flex-1 px-1.5 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] text-[10px]">
              <option value="both">Portal + API</option>
              <option value="api">API Only</option>
              <option value="portal">Portal Only</option>
            </select>
            {apiVersion === "v1" && (
              <label className={`flex items-center gap-1 cursor-pointer px-1.5 py-1 rounded border text-[10px] font-semibold whitespace-nowrap ${
                hasChildId ? "border-[var(--color-primary)] text-[var(--color-primary)] bg-[var(--color-primary)]/10" : "border-[var(--border-color)] text-[var(--text-muted)]"
              }`}>
                <input type="checkbox" checked={hasChildId} onChange={e => setHasChildId(e.target.checked)} className="w-2.5 h-2.5 accent-[var(--color-primary)]" />
                Child ID
              </label>
            )}
          </div>

          <div className="flex gap-1.5 flex-wrap">
            {["kyc", "kyb", "idv", "aml", "risk", "sso"].map(c => (
              <label key={c} className={`flex items-center gap-1 cursor-pointer text-[10px] ${checks[c] ? "text-emerald-500" : "text-[var(--text-muted)]"}`}>
                <input type="checkbox" checked={checks[c]} onChange={e => setChecks(p => ({ ...p, [c]: e.target.checked }))} className="w-2.5 h-2.5 accent-emerald-500" />
                {c.toUpperCase()}
              </label>
            ))}
          </div>
        </div>

        {/* Section list */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {visibleSections.map(s => {
            const isStaticSec = isStatic(s.id);
            const cfg = isStaticSec ? { color: "#22c55e", icon: "\u2713" } : (STATUS_CONFIG[status[s.id]] || STATUS_CONFIG.undefined);
            const isActive = active === s.id;
            const hasTpl = !isStaticSec && hasTemplateForSection(apiVersion, s.id);
            const sidebarKeyGroup = KEY_GROUPS[s.id];
            const sidebarKeyColor = sidebarKeyGroup ? KEY_COLORS[sidebarKeyGroup] : null;
            return (
              <div key={s.id} onClick={() => setActive(s.id)}
                className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer transition-colors ${
                  isActive ? "bg-[var(--color-primary)]/10" : "hover:bg-[var(--bg-tertiary)]"
                }`}
                style={{ borderLeft: `3px solid ${isActive ? (sidebarKeyColor ? sidebarKeyColor.border : "var(--color-primary)") : "transparent"}` }}>
                <span style={{ color: cfg.color }} className="text-[10px] w-3 text-center shrink-0">{cfg.icon}</span>
                <span className={`text-[11px] leading-tight flex-1 ${isActive ? "text-[var(--text-primary)] font-medium" : "text-[var(--text-secondary)]"}`}>{s.emoji} {s.title}</span>
                {sidebarKeyGroup && <span style={{ color: sidebarKeyColor.border }} className="text-[8px] font-bold shrink-0 opacity-80">{sidebarKeyGroup}</span>}
                {hasTpl && <span title="Template saved" className="text-[8px] text-amber-400 shrink-0">TPL</span>}
              </div>
            );
          })}
        </div>

        {/* Bottom actions */}
        <div className="p-3 border-t border-[var(--border-color)] space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1 bg-[var(--bg-tertiary)] rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full transition-all duration-300" style={{ width: `${(approvedCount / visibleSections.length) * 100}%` }} />
            </div>
            <span className="text-[10px] text-[var(--text-muted)] whitespace-nowrap">{approvedCount}/{visibleSections.length}</span>
          </div>
          {tplCount > 0 && (
            <button onClick={loadAllTpls} disabled={!canGenerate}
              className={`w-full py-1.5 rounded text-[11px] font-semibold border-none cursor-pointer transition-colors ${
                canGenerate ? "bg-amber-400 text-gray-900 hover:bg-amber-500" : "bg-[var(--bg-tertiary)] text-[var(--text-muted)] cursor-not-allowed opacity-50"
              }`}>
              Load Templates ({tplCount})
            </button>
          )}
          <button onClick={genAll} disabled={!canGenerate}
            className={`w-full py-1.5 rounded text-[11px] font-medium border-none cursor-pointer transition-colors ${
              canGenerate ? "bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)]" : "bg-[var(--bg-tertiary)] text-[var(--text-muted)] cursor-not-allowed opacity-50"
            }`}>
            Generate All (AI)
          </button>
          <button onClick={() => setPdfMode(true)}
            className={`w-full py-1.5 rounded text-[11px] font-semibold border-none cursor-pointer transition-colors ${
              approvedCount === visibleSections.length ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-emerald-500/20 text-emerald-500"
            }`}>
            PDF {approvedCount < visibleSections.length ? `(${approvedCount}/${visibleSections.length})` : ""}
          </button>
          <div className="flex gap-1.5">
            <button onClick={() => {
              const blob = new Blob([JSON.stringify(templates, null, 2)], { type: "application/json" });
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = "hdb_templates_export.json";
              a.click();
              URL.revokeObjectURL(a.href);
            }} className="flex-1 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-tertiary)] text-[var(--text-muted)] text-[10px] cursor-pointer hover:text-[var(--text-secondary)] transition-colors">
              Export
            </button>
            <label className="flex-1 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-tertiary)] text-[var(--text-muted)] text-[10px] cursor-pointer text-center hover:text-[var(--text-secondary)] transition-colors">
              Import
              <input type="file" accept=".json" className="hidden" onChange={async e => {
                const file = e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = async () => {
                  try {
                    const data = JSON.parse(reader.result);
                    const items = Array.isArray(data) ? data : [];
                    for (const t of items) {
                      if (t.api_version && t.section_id && t.name && t.html) {
                        await helpDocTemplatesApi.save({ apiVersion: t.api_version, sectionId: t.section_id, name: t.name, html: t.html });
                      }
                    }
                    const res = await helpDocTemplatesApi.list(apiVersion);
                    if (res.success && res.data) setTemplates(res.data.data || []);
                  } catch (err) { alert("Invalid JSON file: " + err.message); }
                };
                reader.readAsText(file);
                e.target.value = "";
              }} />
            </label>
          </div>
        </div>
      </div>

      {/* Main panel */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Header bar */}
        <div className="px-5 py-3 bg-[var(--bg-primary)] border-b border-[var(--border-color)] flex items-center justify-between shrink-0">
          <div>
            <div className="font-semibold text-sm text-[var(--text-primary)]">{sec?.emoji} {sec?.title}</div>
            <div className="text-xs text-[var(--text-muted)] mt-0.5">{sec?.hint}</div>
          </div>
          <div className="flex gap-2 items-center">
            {!isStatic(active) && <span className="text-[11px] font-semibold" style={{ color: sc.color }}>{sc.icon} {sc.label}</span>}

            {/* Template picker dropdown */}
            {!isStatic(active) && activeTplList.length > 0 && (!st || st === "error") && (
              <select
                onChange={e => { if (e.target.value) loadTpl(active, e.target.value); e.target.value = ""; }}
                disabled={!canGenerate}
                className={`px-2 py-1 rounded text-xs font-semibold border-none cursor-pointer ${
                  canGenerate ? "bg-amber-400 text-gray-900" : "bg-[var(--bg-tertiary)] text-[var(--text-muted)] cursor-not-allowed opacity-50"
                }`}>
                <option value="">Load Template ({activeTplList.length})</option>
                {activeTplList.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
              </select>
            )}

            {/* Empty / error state generate */}
            {!isStatic(active) && (!st || st === "error") && (
              <button onClick={() => gen(active)} disabled={!canGenerate}
                className={`px-3 py-1 rounded text-xs border-none cursor-pointer transition-colors ${
                  canGenerate ? "bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)]" : "bg-[var(--bg-tertiary)] text-[var(--text-muted)] cursor-not-allowed opacity-50"
                }`}>
                {st === "error" ? "Retry (AI)" : "Generate (AI)"}
              </button>
            )}

            {/* Ready/approved state */}
            {!isStatic(active) && st === "ready" && !editing[active] && (
              <button onClick={() => startEdit(active)} className="px-2.5 py-1 rounded text-xs border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-secondary)] cursor-pointer hover:bg-[var(--bg-tertiary)] transition-colors">Edit HTML</button>
            )}
            {!isStatic(active) && st === "ready" && editing[active] && (
              <button onClick={() => saveEdit(active)} className="px-2.5 py-1 rounded text-xs border-none bg-emerald-500 text-white font-semibold cursor-pointer hover:bg-emerald-600 transition-colors">Save</button>
            )}
            {!isStatic(active) && (st === "ready" || st === "approved") && (
              <>
                {activeTplList.length > 0 && (
                  <select
                    onChange={e => { if (e.target.value) loadTpl(active, e.target.value); e.target.value = ""; }}
                    className="px-2 py-1 rounded text-xs font-semibold border border-amber-400 bg-amber-400/10 text-amber-600 cursor-pointer">
                    <option value="">Load Template ({activeTplList.length})</option>
                    {activeTplList.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                  </select>
                )}
                <button onClick={() => saveTpl(active)}
                  disabled={isSavedAsTpl}
                  className={`px-2.5 py-1 rounded text-xs font-semibold border-none cursor-pointer transition-colors ${
                    isSavedAsTpl ? "bg-[var(--bg-tertiary)] text-[var(--text-muted)] cursor-not-allowed opacity-50" : "bg-amber-400 text-gray-900 hover:bg-amber-500"
                  }`}>
                  {isSavedAsTpl ? "Template Saved" : "Save as Template"}
                </button>
                {st === "ready" && <button onClick={() => approve(active)} className="px-3 py-1 rounded text-xs font-bold border-none bg-emerald-500 text-white cursor-pointer hover:bg-emerald-600 transition-colors">Approve</button>}
                {st === "approved" && <button onClick={() => setStatus(p => ({ ...p, [active]: "ready" }))} className="px-2.5 py-1 rounded text-xs border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-secondary)] cursor-pointer hover:bg-[var(--bg-tertiary)] transition-colors">Unapprove</button>}
              </>
            )}
          </div>
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-y-auto p-5 bg-[var(--bg-secondary)]">

          {(active === "recipes" || active === "workflows") && (
            <div className="hdb-content rounded-lg p-5 border border-[var(--border-color)] bg-[var(--bg-primary)] shadow-sm">
              {active === "workflows"
                ? <WorkflowsDisplay recipes={RECIPES} checkBadge={CHECK_BADGE} />
                : <RecipesDisplay pending={true} recipes={RECIPES} checkBadge={CHECK_BADGE} />}
            </div>
          )}

          {active === "diagrams" && (
            <div className="hdb-content rounded-lg p-5 border border-[var(--border-color)] bg-[var(--bg-primary)] shadow-sm">
              <SequenceDiagrams checks={checks} apiVersion={apiVersion} />
            </div>
          )}

          {active === "int_diagrams" && (
            <div className="hdb-content rounded-lg p-5 border border-[var(--border-color)] bg-[var(--bg-primary)] shadow-sm">
              <IntegrationDiagrams checks={checks} />
            </div>
          )}

          {!isStatic(active) && !st && (
            <div className="text-center py-16 px-5 text-[var(--text-muted)]">
              <div className="text-4xl mb-3">{activeTpl ? "\uD83D\uDCC4" : "\u26A1"}</div>
              <div className="text-sm font-medium mb-1.5 text-[var(--text-secondary)]">
                {activeTpl ? "Template available" : "Ready to generate"}
              </div>
              <div className="text-xs mb-5 text-[var(--text-muted)]">
                {!canGenerate
                  ? "Enter a client name in the sidebar to get started."
                  : activeTpl
                    ? "Load the saved template instantly, or generate fresh content with AI."
                    : "Click Generate to create this section using AI, or save a template after generating once."
                }
              </div>
              <div className="flex gap-3 justify-center items-center">
                {activeTplList.length > 0 && (
                  <select
                    onChange={e => { if (e.target.value) loadTpl(active, e.target.value); e.target.value = ""; }}
                    disabled={!canGenerate}
                    className={`px-3 py-2 rounded-md text-sm font-semibold border-none cursor-pointer ${
                      canGenerate ? "bg-amber-400 text-gray-900" : "bg-[var(--bg-tertiary)] text-[var(--text-muted)] cursor-not-allowed opacity-50"
                    }`}>
                    <option value="">Load Template ({activeTplList.length})</option>
                    {activeTplList.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                  </select>
                )}
                <button onClick={() => gen(active)} disabled={!canGenerate}
                  className={`px-5 py-2 rounded-md text-sm border-none cursor-pointer transition-colors ${
                    canGenerate ? "bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)]" : "bg-[var(--bg-tertiary)] text-[var(--text-muted)] cursor-not-allowed opacity-50"
                  }`}>
                  Generate (AI)
                </button>
              </div>
            </div>
          )}

          {!isStatic(active) && st === "generating" && (
            <div className="text-center py-16 px-5 text-[var(--color-info)]">
              <div className="w-9 h-9 border-[3px] border-[var(--color-info)]/20 border-t-[var(--color-info)] rounded-full mx-auto mb-4" style={{ animation: "hdb-spin 0.8s linear infinite" }} />
              <div className="text-sm font-medium mb-1.5 text-[var(--text-primary)]">Generating section...</div>
              <div className="text-xs text-[var(--text-muted)]" style={{ animation: "hdb-pulse 1.5s ease-in-out infinite" }}>This may take a few seconds</div>
            </div>
          )}

          {!isStatic(active) && (st === "ready" || st === "approved") && (
            <div className={`rounded-lg p-5 shadow-sm bg-[var(--bg-primary)] ${st === "approved" ? "border-2 border-emerald-500" : "border border-[var(--border-color)]"}`}>
              {st === "approved" && (
                <div className="bg-emerald-500/10 border border-emerald-500 rounded px-3 py-1.5 mb-4 text-xs text-emerald-600 dark:text-emerald-400 font-semibold">
                  Approved — this section will be included in the PDF export
                </div>
              )}
              {activeTplList.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-400 rounded px-3 py-1.5 mb-4 text-xs text-amber-700 dark:text-amber-400">
                  <div className="flex items-center justify-between mb-1">
                    <span>{activeTplList.length} template{activeTplList.length > 1 ? "s" : ""} saved</span>
                  </div>
                  {activeTplList.map(t => (
                    <div key={t.name} className="flex items-center justify-between py-0.5">
                      <span className="font-semibold">{t.name}</span>
                      <div className="flex gap-2">
                        <button onClick={() => loadTpl(active, t.name)} className="bg-transparent border-none text-amber-600 dark:text-amber-400 cursor-pointer text-[10px] underline">Load</button>
                        <button onClick={() => deleteTpl(active, t.name)} className="bg-transparent border-none text-red-500 cursor-pointer text-[10px] underline">Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {editing[active]
                ? <textarea value={editBuf[active] || ""} onChange={e => setEditBuf(p => ({ ...p, [active]: e.target.value }))}
                    className="w-full min-h-[420px] font-mono text-xs border border-[var(--border-color)] rounded p-3 bg-[var(--bg-secondary)] text-[var(--text-primary)] leading-relaxed" style={{ boxSizing: "border-box" }} />
                : <div>
                    <div className="hdb-content text-[var(--text-primary)]" dangerouslySetInnerHTML={{ __html: content[active] || "" }} style={{ lineHeight: 1.7 }} />
                    {active === "overview" && apiDocs && <PinnedCurl apiVersion={apiVersion} />}
                    {active === "idv" && <SampleIdDisplay />}
                  </div>
              }
            </div>
          )}

          {!isStatic(active) && (st === "ready" || st === "approved") && (
            <div className="mt-4 rounded-lg p-4 border border-[var(--border-color)] bg-[var(--bg-primary)] shadow-sm">
              <div className="text-xs font-semibold text-[var(--text-secondary)] mb-2">Refine this section</div>
              <div className="flex gap-2">
                <input value={feedback[active] || ""} onChange={e => setFeedback(p => ({ ...p, [active]: e.target.value }))}
                  onKeyDown={e => e.key === "Enter" && gen(active, true)}
                  placeholder="e.g. 'Add more code examples' or 'Include the webhook payload structure'..."
                  className="flex-1 px-3 py-1.5 border border-[var(--border-color)] rounded bg-[var(--bg-secondary)] text-[var(--text-primary)] text-xs placeholder:text-[var(--text-muted)]" />
                <button onClick={() => gen(active, true)}
                  className="px-3 py-1.5 rounded text-xs border-none bg-[var(--bg-tertiary)] text-[var(--text-primary)] cursor-pointer hover:bg-[var(--border-color)] transition-colors">
                  Regenerate (AI)
                </button>
              </div>
              <div className="text-[10px] text-[var(--text-muted)] mt-1.5">Press Enter or click Regenerate to apply feedback. This will call the AI to generate fresh content.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
